import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { TextValidator } from "anketa-core/validation";

export const setupOctopusGroup = (octopusScanners: Section, idx = 0) => {
  const octopusGroup = octopusScanner(idx);
  octopusScanners.addChild(octopusGroup.octopusScanner);

  function onConfigChange() {
    const name = octopusGroup.octopusName.fact.raw;

    if (name && name.toString().trim() !== "") {
      octopusGroup.octopusName.removeChangeListener(onConfigChange);
      setupOctopusGroup(octopusScanners, idx + 1);
    }
  }

  octopusGroup.octopusName.addChangeListener(onConfigChange);
};

export const octopusScanner = (
  idx: number
): { octopusScanner: Group; octopusName: OpenQuestion } => {
  const idPrefix = `octopus_scanner_${idx}`;

  const octopusScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured Octopus Scanner"),
    new Translation("de", "Unkonfigurierter Octopus Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();
  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      octopusScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      octopusScanner.title = [
        new Translation("en", "Unconfigured Octopus Scanner"),
        new Translation("de", "Unkonfigurierter Octopus Scanner"),
      ];
    }
  });
  octopusScanner.addChild(name);

  const octopusUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "Octopus Cloud API URL"),
    new Translation("de", "Octopus Cloud API-URL"),
  ]);
  octopusUrl.validator = new TextValidator(TextValidatorType.Uri);
  octopusUrl.required = true;
  octopusUrl.help = [
    new Translation(
      "en",
      "The full URL to your Octopus Cloud API instance (e.g., https://api.octopus.cloud/v1)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zu Ihrer Octopus Cloud API-Instanz (z.B. https://api.octopus.cloud/v1)."
    ),
  ];
  octopusScanner.addChild(octopusUrl);

  const octopusWorkspaceId = new OpenQuestion(`${idPrefix}_workspace_id`, [
    new Translation("en", "Workspace ID"),
    new Translation("de", "Workspace-ID"),
  ]);
  octopusWorkspaceId.validator = new TextValidator();
  octopusWorkspaceId.required = true;
  octopusWorkspaceId.help = [
    new Translation(
      "en",
      "The unique identifier of your Octopus Cloud workspace."
    ),
    new Translation(
      "de",
      "Die eindeutige Kennung Ihres Octopus Cloud-Workspaces."
    ),
  ];
  octopusScanner.addChild(octopusWorkspaceId);

  const octopusApiToken = new OpenQuestion(`${idPrefix}_api_token`, [
    new Translation("en", "API Token"),
    new Translation("de", "API-Token"),
  ]);
  octopusApiToken.required = true;
  octopusApiToken.readonly = true;
  octopusApiToken.help = [
    new Translation(
      "en",
      "A personal API token with read-only access to devices in your Octopus Cloud workspace."
    ),
    new Translation(
      "de",
      "Ein persönliches API-Token mit Nur-Lesen-Zugriff auf Geräte in Ihrem Octopus Cloud-Workspace."
    ),
  ];
  octopusScanner.addChild(octopusApiToken);

  const octopusOcid = new OpenQuestion(`${idPrefix}_ocid`, [
    new Translation("en", "OCID"),
    new Translation("de", "OCID"),
  ]);
  octopusOcid.validator = new TextValidator();
  octopusOcid.required = true;
  octopusOcid.help = [
    new Translation(
      "en",
      "The Octopus Cloud OCID (Octopus Cloud Identifier) required for API access."
    ),
    new Translation(
      "de",
      "Die Octopus Cloud OCID (Octopus Cloud Identifier), die für den API-Zugriff benötigt wird."
    ),
  ];
  octopusScanner.addChild(octopusOcid);

  return { octopusScanner, octopusName: name };
};
